import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionHeader from '@/components/feature/SectionHeader';
import WorkCard from '@/components/feature/WorkCard';
import { works } from '@/mocks/works';
import { useSeo } from '@/hooks/useSeo';
import { useFavorites } from '@/hooks/useFavorites';

interface ToastState {
  text: string;
  key: number;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy approach
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const work = works.find((item) => item.id === id);
  const categoryName = work?.type === 'tv' ? '电视剧' : '电影';

  useSeo({
    title: work ? `${work.title}详情介绍 - 美丽小蜜桃3` : '未找到该影视 - 美丽小蜜桃3',
    description: work
      ? `${work.title}是一部${work.year}年${work.region}${categoryName}，由${work.director}执导，${work.actors
          .slice(0, 3)
          .join('、')}主演。${work.summary.slice(0, 60)}`
      : '未找到相关影视资源，返回美丽小蜜桃3影视大全浏览更多内容。',
    keywords: work ? `美丽小蜜桃3,${work.title},${work.title}剧情介绍,${work.title}演员表,${categoryName}` : '美丽小蜜桃3,影视大全',
  });

  const { isFavorite, toggle } = useFavorites();
  const [toast, setToast] = useState<ToastState | null>(null);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!work) {
    return (
      <SiteLayout>
        <div className="container-page flex flex-col items-center justify-center py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <h1 className="mt-4 text-xl font-bold text-foreground-950">未找到该影视资源</h1>
          <p className="mt-2 text-sm text-foreground-500">该作品可能已下线或链接有误</p>
          <Link
            to="/"
            className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const favorite = isFavorite(work.id);
  const related = works
    .filter((item) => item.type === work.type && item.id !== work.id)
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, 5);

  const handleFavorite = () => {
    const added = toggle(work.id);
    setToast({ text: added ? '已加入收藏，可在详情页查看收藏状态' : '已取消收藏', key: Date.now() });
  };

  const handleShare = async () => {
    const ok = await copyToClipboard(window.location.href);
    setToast({ text: ok ? '页面链接已复制到剪贴板' : '复制失败，请手动复制浏览器地址栏链接', key: Date.now() });
  };

  const infoRows = [
    { label: '类型', value: categoryName },
    { label: '年份', value: `${work.year} 年` },
    { label: '地区', value: work.region },
    { label: work.type === 'tv' ? '集数' : '片长', value: work.type === 'tv' ? `全 ${work.episodes} 集` : '剧情长片' },
    { label: '导演', value: work.director },
  ];

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="container-page py-6 md:py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: `${categoryName}大全`, to: `/category/${work.type}` },
              { label: work.title },
            ]}
          />
        </div>
      </section>

      <section className="container-page pt-6 md:pt-10">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr] lg:gap-10">
          <div className="mx-auto w-full max-w-[260px] lg:mx-0 lg:max-w-none">
            <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-200">
              <img
                src={work.cover}
                alt={`《${work.title}》高清封面`}
                title={`${work.title} 剧情介绍与演员表`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex flex-col">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">{categoryName}</span>
              <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">{work.region}</span>
              <span className="rounded-md bg-background-200 px-2.5 py-1 text-xs text-foreground-700">{work.year}</span>
              {work.type === 'tv' && (
                <span className="rounded-md bg-accent-100 px-2.5 py-1 text-xs text-accent-900">更新中 · 全 {work.episodes} 集</span>
              )}
            </div>

            <h1 className="mt-3 text-2xl font-bold text-foreground-950 md:text-4xl">{work.title}</h1>

            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, index) => (
                  <i
                    key={index}
                    className={index < Math.round(work.rating / 2) ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}
                  />
                ))}
              </span>
              <span className="text-lg font-bold text-primary-600">{work.rating.toFixed(1)}</span>
              <span className="text-sm text-foreground-500">/ 10 分</span>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {work.genres.map((genre) => (
                <Link
                  key={genre}
                  to={`/search?q=${encodeURIComponent(genre)}`}
                  className="cursor-pointer rounded-full bg-background-100 px-3.5 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  {genre}
                </Link>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              {infoRows.map((row) => (
                <div key={row.label} className="flex items-start gap-3 text-sm">
                  <span className="w-14 shrink-0 text-foreground-500">{row.label}</span>
                  <span className="text-foreground-900">{row.value}</span>
                </div>
              ))}
              <div className="flex items-start gap-3 text-sm sm:col-span-2">
                <span className="w-14 shrink-0 text-foreground-500">主演</span>
                <span className="flex flex-wrap gap-x-3 gap-y-1 text-foreground-900">
                  {work.actors.map((actor) => (
                    <Link
                      key={actor}
                      to={`/search?q=${encodeURIComponent(actor)}`}
                      className="cursor-pointer transition-colors hover:text-primary-600"
                    >
                      {actor}
                    </Link>
                  ))}
                </span>
              </div>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors ${
                  favorite
                    ? 'bg-accent-500 text-foreground-950 hover:bg-accent-600'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
              >
                <i className={favorite ? 'ri-heart-3-fill' : 'ri-heart-3-line'} />
                {favorite ? '已收藏' : '收藏本片'}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-share-forward-line" />
                复制分享链接
              </button>
              <Link
                to={`/category/${work.type}`}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-secondary-100 px-5 py-2.5 text-sm font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                <i className="ri-stack-line" />
                更多{categoryName}
              </Link>
            </div>

            <p className="mt-5 flex items-center gap-2 rounded-md bg-background-100 px-4 py-3 text-xs text-foreground-600">
              <i className="ri-information-line text-base text-secondary-600" />
              本站仅提供影视内容介绍与信息查询，不提供任何播放或下载服务。
            </p>
          </div>
        </div>
      </section>

      <section className="container-page pt-10 md:pt-14">
        <SectionHeader title="剧情简介" />
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <p className="text-sm leading-relaxed text-foreground-700 md:text-base">{work.summary}</p>
          <div className="mt-5 grid grid-cols-1 gap-3 border-t border-background-200 pt-5 sm:grid-cols-3">
            <div>
              <p className="text-xs text-foreground-500">类型标签</p>
              <p className="mt-1 text-sm font-medium text-foreground-900">{work.genres.join(' · ')}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500">导演</p>
              <p className="mt-1 text-sm font-medium text-foreground-900">{work.director}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500">主演阵容</p>
              <p className="mt-1 text-sm font-medium text-foreground-900">{work.actors.join('、')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-page pt-10 md:pt-14">
        <SectionHeader
          title="相关推荐"
          subtitle={`喜欢《${work.title}》的话，这些同类型作品也别错过`}
          moreTo={`/category/${work.type}`}
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 lg:gap-5">
          {related.map((item) => (
            <WorkCard key={item.id} work={item} />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 rounded-md bg-foreground-950 px-5 py-3 text-sm text-background-50 shadow-lg">
          {toast.text}
        </div>
      )}
    </SiteLayout>
  );
}